/**
 * Mail Inbox Page - Zeigt ausstehende Mail-Entscheidungen für UMitza
 * 
 * Features:
 * - Decision Templates mit CRUD (Admin-only für Anlegen/Bearbeiten)
 * - Eigentümer-Zuordnung (automatisch + manuell)
 * - Audit-Trail für Template-Änderungen
 * 
 * BUILD: 2025-01-25-v2
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  Mail, Clock, User, Paperclip, ChevronRight, AlertCircle, Check, 
  Trash2, Forward, FileText, RefreshCw, Plus, Edit2, X, Save,
  Users, Search, Zap, ExternalLink, Eye, Tag
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MailInboxPage = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [deciding, setDeciding] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    prompt_text: '',
    default_category: 'sonstiges',
    default_visibility: 'admin_only',
    default_actions: [],
    description: '',
    ai_context_hint: '',
    active: true
  });
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Owners for manual assignment
  const [owners, setOwners] = useState([]);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [loadingOwners, setLoadingOwners] = useState(false);
  
  // Debug Mail-Suche
  const [debugQuery, setDebugQuery] = useState('linkedin');
  const [debugResult, setDebugResult] = useState(null);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debugLoading, setDebugLoading] = useState(false);

  // Original-Mail Anzeige
  const [showOriginalModal, setShowOriginalModal] = useState(false);
  const [originalContent, setOriginalContent] = useState(null);
  const [loadingOriginal, setLoadingOriginal] = useState(false);

  // Sync State
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Auto-Archive Toggle (v2.9.4)
  const [autoArchive, setAutoArchive] = useState(true);

  // Reply-Templates (v2.9.4)
  const [replyTemplates, setReplyTemplates] = useState([]);
  const [showReplyDraftModal, setShowReplyDraftModal] = useState(false);
  const [selectedReplyTemplate, setSelectedReplyTemplate] = useState(null);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [draftResult, setDraftResult] = useState(null);

  // Quick-Decide State (v2.9.5)
  const [quickDeciding, setQuickDeciding] = useState(null); // aktiver Quick-Action key
  const [quickActions, setQuickActions] = useState([]); // v2.9.6: aus DB geladen

  // Smart Suggestion State (v2.9.7)
  const [smartSuggestion, setSmartSuggestion] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);

  // KI-Antwort State (v2.9.8)
  const [showAiReplyModal, setShowAiReplyModal] = useState(false);
  const [generatingAiReply, setGeneratingAiReply] = useState(false);
  const [aiReplyResult, setAiReplyResult] = useState(null);
  const [aiReplyText, setAiReplyText] = useState('');
  const [creatingAiDraft, setCreatingAiDraft] = useState(false);

  // Decision form state
  const [decision, setDecision] = useState({
    category: '',
    email_visibility: '',
    action: '',
    template_id: '',
    owner_id: '',
    forward_to: 'rechnung@gb-sylt.de',
    create_rule: false,
    rule_match_type: 'sender',
    rule_match_value: '',
    rule_approved: false,
    add_owner_email: false,
    owner_id_to_update: '',
    new_owner_email: ''
  });

  const isAdmin = user?.role === 'admin' || user?.username === 'UMitza';

  const getToken = () => {
    const savedUser = localStorage.getItem('gb-sylt-user');
    if (savedUser) {
      try {
        return JSON.parse(savedUser).token;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  // Load inbox messages
  const loadInbox = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/mail/inbox`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Laden der Inbox');
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync Mail Inbox mit Outlook - NUR aktive Mailbox
  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const token = getToken();
      // Hole Mail-Konten
      const accountsResponse = await axios.get(`${API}/mail/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const accounts = accountsResponse.data.accounts || [];
      if (accounts.length === 0) {
        setSyncResult({ error: 'Kein Mail-Konto konfiguriert' });
        return;
      }
      
      // NUR ulrich.mitza@gb-sylt.de synchronisieren (aktive Mailbox)
      const activeMailbox = accounts.find(a => a.email === 'ulrich.mitza@gb-sylt.de');
      if (!activeMailbox) {
        setSyncResult({ error: 'Aktive Mailbox (ulrich.mitza@gb-sylt.de) nicht gefunden' });
        return;
      }
      
      if (activeMailbox.connection_status !== 'connected') {
        setSyncResult({ error: 'Mailbox nicht verbunden - bitte zuerst Verbindung testen' });
        return;
      }
      
      // Sync NUR die aktive Mailbox
      const response = await axios.post(
        `${API}/mail/accounts/${activeMailbox.id}/sync-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSyncResult({
        success: true,
        new_messages: response.data.inbox?.new_messages || 0,
        skipped: response.data.inbox?.skipped_duplicates || 0,
        mailbox: activeMailbox.email,
        errors: response.data.inbox?.error ? [response.data.inbox.error] : null
      });
      
      // Inbox neu laden
      await loadInbox();
      
      // Success-Meldung nach 4 Sekunden ausblenden
      setTimeout(() => setSyncResult(null), 4000);
      
    } catch (err) {
      setSyncResult({ error: err.response?.data?.detail || 'Sync fehlgeschlagen' });
    } finally {
      setSyncing(false);
    }
  };

  // Load decision templates
  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/mail/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Fehler beim Laden der Templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  // Debug Mail-Suche ausführen
  const runDebugMailSearch = async () => {
    if (!debugQuery.trim()) return;
    
    setDebugLoading(true);
    setDebugResult(null);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/mail/debug-search?query=${encodeURIComponent(debugQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDebugResult(response.data);
    } catch (err) {
      setDebugResult({ 
        success: false, 
        error: err.response?.data?.detail || err.message 
      });
    } finally {
      setDebugLoading(false);
    }
  };

  // Original-Mail laden und anzeigen
  const loadOriginalMail = async (messageId) => {
    setLoadingOriginal(true);
    setOriginalContent(null);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/mail/messages/${messageId}/original`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOriginalContent(response.data);
      setShowOriginalModal(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Laden der Original-Mail');
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoadingOriginal(false);
    }
  };

  // In Outlook öffnen
  const openInOutlook = (webLink) => {
    if (webLink) {
      window.open(webLink, '_blank', 'noopener,noreferrer');
    } else {
      setError('Outlook Web Link nicht verfügbar');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Reply-Templates laden (v2.9.4)
  const loadReplyTemplates = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/mail/reply-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReplyTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Reply-Templates laden fehlgeschlagen:', err);
    }
  }, []);

  // Quick-Actions laden (v2.9.6: aus DB)
  const loadQuickActions = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/mail/quick-actions/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuickActions(response.data.quick_actions || []);
    } catch (err) {
      console.error('Quick-Actions laden fehlgeschlagen:', err);
    }
  }, []);

  // Smart Suggestion laden (v2.9.7, erweitert v2.9.10 mit Automationsmodus)
  const loadSmartSuggestion = useCallback(async (messageId) => {
    if (!messageId) {
      setSmartSuggestion(null);
      return;
    }
    setLoadingSuggestion(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/mail/inbox/${messageId}/smart-suggestion`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.has_suggestion) {
        const suggestion = response.data.suggestion;
        setSmartSuggestion(suggestion);

        // v2.9.10: Automatische Ausfuehrung wenn can_auto_execute
        if (suggestion.can_auto_execute) {
          try {
            const autoRes = await axios.post(`${API}/mail/inbox/${messageId}/auto-execute`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (autoRes.data.success) {
              setSuccessMessage(`Automatisch verarbeitet: ${suggestion.quick_action?.button_label}`);
              setSmartSuggestion(prev => prev ? {...prev, auto_executed: true} : null);
              setTimeout(() => {
                loadMessages();
                setSuccessMessage(null);
              }, 3000);
              return;
            }
          } catch (autoErr) {
            console.error('Auto-Execute fehlgeschlagen, zeige als Vorschlag:', autoErr);
          }
        }
      } else {
        setSmartSuggestion(null);
      }
    } catch (err) {
      console.error('Smart Suggestion laden fehlgeschlagen:', err);
      setSmartSuggestion(null);
    } finally {
      setLoadingSuggestion(false);
    }
  }, []);

  // Suggestion Feedback senden (v2.9.7)
  const sendSuggestionFeedback = useCallback(async (messageId, suggestedAction, accepted, confidence) => {
    try {
      const token = getToken();
      await axios.post(`${API}/mail/inbox/${messageId}/suggestion-feedback`, {
        suggested_quick_action: suggestedAction,
        accepted,
        confidence: confidence || 0,
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
    } catch (err) {
      console.error('Suggestion Feedback fehlgeschlagen:', err);
    }
  }, []);

  // KI-Antwortvorschlag generieren (v2.9.8)
  const handleGenerateAiReply = useCallback(async () => {
    if (!selectedMessage) return;
    setShowAiReplyModal(true);
    setGeneratingAiReply(true);
    setAiReplyResult(null);
    setAiReplyText('');

    try {
      const token = getToken();
      const response = await axios.post(
        `${API}/mail/inbox/${selectedMessage.id}/generate-ai-reply`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAiReplyResult(response.data);
        setAiReplyText(response.data.reply_text || '');
      } else {
        setAiReplyResult({ success: false, error: response.data.error || 'Unbekannter Fehler' });
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.message || 'Fehler bei KI-Generierung';
      setAiReplyResult({ success: false, error: errMsg });
    } finally {
      setGeneratingAiReply(false);
    }
  }, [selectedMessage]);

  // KI-Draft in Outlook erstellen (v2.9.8)
  const handleCreateAiDraft = useCallback(async () => {
    if (!selectedMessage || !aiReplyText.trim()) return;
    setCreatingAiDraft(true);

    try {
      const token = getToken();
      const wasEdited = aiReplyText !== (aiReplyResult?.reply_text || '');

      const response = await axios.post(
        `${API}/mail/inbox/${selectedMessage.id}/create-draft-from-ai`,
        {
          reply_text: aiReplyText,
          generation_id: aiReplyResult?.generation_id || '',
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      // Feedback loggen
      const feedbackAction = wasEdited ? 'edited_and_accepted' : 'accepted';
      const feedbackForm = new FormData();
      feedbackForm.append('action', feedbackAction);
      feedbackForm.append('generation_id', aiReplyResult?.generation_id || '');
      await axios.post(
        `${API}/mail/inbox/${selectedMessage.id}/ai-reply-feedback`,
        feedbackForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessMessage(
          response.data.preview_mode
            ? 'Preview: KI-Entwurf simuliert (nicht in Outlook)'
            : 'KI-Antwort-Entwurf in Outlook gespeichert!'
        );
        setDraftResult(response.data);
        setShowAiReplyModal(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Erstellen des Entwurfs');
    } finally {
      setCreatingAiDraft(false);
    }
  }, [selectedMessage, aiReplyText, aiReplyResult]);

  // KI-Vorschlag verwerfen (v2.9.8)
  const handleDiscardAiReply = useCallback(async () => {
    if (selectedMessage && aiReplyResult?.generation_id) {
      try {
        const token = getToken();
        const feedbackForm = new FormData();
        feedbackForm.append('action', 'discarded');
        feedbackForm.append('generation_id', aiReplyResult.generation_id);
        await axios.post(
          `${API}/mail/inbox/${selectedMessage.id}/ai-reply-feedback`,
          feedbackForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.error('AI Feedback fehlgeschlagen:', err);
      }
    }
    setShowAiReplyModal(false);
    setAiReplyResult(null);
    setAiReplyText('');
  }, [selectedMessage, aiReplyResult]);

  // Reply-Draft erstellen (v2.9.4)
  const createReplyDraft = async (messageId, templateId) => {
    setCreatingDraft(true);
    setDraftResult(null);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('template_id', templateId);
      
      const response = await axios.post(`${API}/mail/inbox/${messageId}/create-reply-draft`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDraftResult(response.data);
      setSuccessMessage(response.data.message || 'Entwurf erstellt');
      setTimeout(() => setSuccessMessage(null), 5000);
      setShowReplyDraftModal(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Erstellen des Entwurfs');
      setTimeout(() => setError(null), 5000);
    } finally {
      setCreatingDraft(false);
    }
  };

  // Search owners for manual assignment
  const searchOwners = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setOwners([]);
      return;
    }
    
    setLoadingOwners(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/mail/owners/search-for-chat?query=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOwners(response.data.owners || []);
    } catch (err) {
      console.error('Fehler bei Eigentümer-Suche:', err);
    } finally {
      setLoadingOwners(false);
    }
  }, []);

  useEffect(() => {
    loadInbox();
    loadTemplates();
    loadReplyTemplates();
    loadQuickActions();
  }, [loadInbox, loadTemplates, loadReplyTemplates, loadQuickActions]);

  // Debounced owner search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (ownerSearch) {
        searchOwners(ownerSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [ownerSearch, searchOwners]);

  const handleSelectMessage = (message) => {
    setSelectedMessage(message);
    setSmartSuggestion(null); // Reset suggestion on selection
    loadSmartSuggestion(message.id); // Load suggestion for this message
    setDecision({
      category: message.category || '',
      email_visibility: message.email_visibility || '',
      action: '',
      template_id: message.decision_template_id || '',
      owner_id: message.owner_id || '',
      forward_to: 'rechnung@gb-sylt.de',
      create_rule: false,
      rule_match_type: 'sender',
      rule_match_value: message.from_address || '',
      rule_approved: false,
      add_owner_email: message.owner_match_type === 'suggested',
      owner_id_to_update: message.owner_id || '',
      new_owner_email: message.from_address || ''
    });
    setOwnerSearch(message.owner_name || '');
  };

  // Apply template to decision
  const applyTemplate = (template) => {
    setDecision(prev => ({
      ...prev,
      template_id: template.id,
      category: template.default_category || prev.category,
      email_visibility: template.default_visibility || prev.email_visibility,
      action: template.default_actions?.[0] || prev.action
    }));
    setSuccessMessage(`Template "${template.name}" angewendet`);
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  // Handle decision submit
  const handleDecide = async () => {
    if (!selectedMessage) return;
    if (!decision.category || !decision.email_visibility || !decision.action) {
      alert('Bitte alle Pflichtfelder ausfüllen (Kategorie, Sichtbarkeit, Aktion)');
      return;
    }

    // Smart Suggestion Feedback: manuelle Entscheidung = Vorschlag ignoriert (v2.9.7)
    if (smartSuggestion) {
      sendSuggestionFeedback(
        selectedMessage.id,
        smartSuggestion.quick_action?.slug,
        false,
        smartSuggestion.confidence
      );
    }

    setDeciding(true);
    try {
      const token = getToken();
      const formData = new FormData();
      
      Object.entries(decision).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          formData.append(key, value.toString());
        }
      });

      // Auto-Archive Parameter (v2.9.4)
      formData.append('auto_archive', autoArchive.toString());

      const response = await axios.post(`${API}/mail/inbox/${selectedMessage.id}/decide`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Prüfe ob Graph-Aktion fehlgeschlagen ist
      if (!response.data.success) {
        // Graph-Aktion fehlgeschlagen - Mail bleibt in Liste
        alert(response.data.message || 'Outlook-Aktion fehlgeschlagen - Mail bleibt in Inbox');
        await loadInbox();  // Aktualisieren, aber Mail bleibt pending
        return;
      }
      
      if (response.data.warning) {
        setSuccessMessage(`Entscheidung gespeichert, aber: ${response.data.warning}`);
      } else if (response.data.template_used) {
        setSuccessMessage(`Entscheidung mit Template "${response.data.template_used}" gespeichert`);
      } else {
        setSuccessMessage(response.data.message || 'Entscheidung gespeichert');
      }
      
      await loadInbox();
      setSelectedMessage(null);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setDeciding(false);
    }
  };

  // 1-Klick Entscheidung (v2.9.5, erweitert v2.9.7 mit Smart Suggestion Feedback)
  const handleQuickDecide = async (actionType, fromSuggestion = false) => {
    if (!selectedMessage) return;
    setQuickDeciding(actionType);

    // Smart Suggestion Feedback (v2.9.7)
    if (smartSuggestion) {
      const accepted = fromSuggestion || actionType === smartSuggestion.quick_action?.slug;
      sendSuggestionFeedback(
        selectedMessage.id,
        smartSuggestion.quick_action?.slug,
        accepted,
        smartSuggestion.confidence
      );
    }

    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('quick_action', actionType);
      formData.append('auto_archive_override', autoArchive.toString());

      const response = await axios.post(
        `${API}/mail/inbox/${selectedMessage.id}/quick-decide`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessMessage(response.data.feedback || response.data.message);
        await loadInbox();
        setSelectedMessage(null);
        setTimeout(() => setSuccessMessage(null), 4000);
      } else {
        setError(response.data.message || 'Aktion fehlgeschlagen');
        setTimeout(() => setError(null), 5000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler bei 1-Klick Entscheidung');
      setTimeout(() => setError(null), 5000);
    } finally {
      setQuickDeciding(null);
    }
  };

  // Template CRUD functions
  const openCreateTemplateModal = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      prompt_text: '',
      default_category: 'sonstiges',
      default_visibility: 'admin_only',
      default_actions: [],
      description: '',
      ai_context_hint: '',
      active: true
    });
    setShowTemplateModal(true);
  };

  const openEditTemplateModal = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name || '',
      prompt_text: template.prompt_text || '',
      default_category: template.default_category || 'sonstiges',
      default_visibility: template.default_visibility || 'admin_only',
      default_actions: template.default_actions || [],
      description: template.description || '',
      ai_context_hint: template.ai_context_hint || '',
      active: template.active !== false
    });
    setShowTemplateModal(true);
  };

  const saveTemplate = async () => {
    if (!templateForm.name.trim()) {
      alert('Name ist erforderlich');
      return;
    }

    setSavingTemplate(true);
    try {
      const token = getToken();
      const formData = new FormData();
      
      formData.append('name', templateForm.name.trim());
      formData.append('prompt_text', templateForm.prompt_text || '');
      formData.append('default_category', templateForm.default_category);
      formData.append('default_visibility', templateForm.default_visibility);
      formData.append('default_actions', JSON.stringify(templateForm.default_actions));
      formData.append('description', templateForm.description || '');
      formData.append('ai_context_hint', templateForm.ai_context_hint || '');
      formData.append('active', templateForm.active);

      if (editingTemplate) {
        // Update
        await axios.put(`${API}/mail/templates/${editingTemplate.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccessMessage('Template aktualisiert');
      } else {
        // Create
        await axios.post(`${API}/mail/templates`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccessMessage('Template erstellt');
      }

      setShowTemplateModal(false);
      await loadTemplates();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler beim Speichern des Templates');
    } finally {
      setSavingTemplate(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const actions = [
    { value: 'archive', label: 'Archivieren', icon: FileText },
    { value: 'none', label: 'Nur kategorisieren', icon: Check },
    { value: 'forward', label: 'Weiterleiten', icon: Forward },
    { value: 'draft', label: 'Antwortentwurf', icon: Mail }
  ];

  const categoryOptions = [
    'werbung', 'eigentuemer', 'anfrage', 'info', 
    'rechnung', 'recht', 'steuer', 'sonstiges'
  ];

  if (loading && messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
              <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Mail Inbox</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {total} ausstehende Entscheidungen
              </p>
            </div>
          </div>
          <button
            onClick={loadInbox}
            disabled={loading}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Inbox neu laden"
            data-testid="refresh-inbox-btn"
          >
            <RefreshCw className={`h-5 w-5 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
          
          {/* Sync mit Outlook Button */}
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors text-sm font-medium"
            title="Neue Mails von Outlook abrufen"
            data-testid="sync-outlook-btn"
          >
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sync...' : 'Sync'}
          </button>
          
          {/* Sync Result Message */}
          {syncResult && (
            <div className={`ml-2 px-3 py-1 rounded text-sm ${
              syncResult.error 
                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            }`}>
              {syncResult.error 
                ? `❌ ${syncResult.error}`
                : `✅ +${syncResult.new_messages} neu${syncResult.errors ? ` (${syncResult.errors.length} Fehler)` : ''}`
              }
            </div>
          )}
          
          {/* Debug Mail-Suche Button */}
          {isAdmin && (
            <button
              onClick={() => setShowDebugModal(true)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ml-2"
              title="Debug: Mail-Suche testen"
              data-testid="debug-mail-search-btn"
            >
              <Search className="h-5 w-5 text-orange-500" />
            </button>
          )}
        </div>
      </div>

      {/* Debug Mail-Suche Modal */}
      {showDebugModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                🔍 Debug: Mail-Suche testen
              </h3>
              <button onClick={() => setShowDebugModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Teste ob der Chat Mails zu einem Suchbegriff findet:
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={debugQuery}
                  onChange={(e) => setDebugQuery(e.target.value)}
                  placeholder="z.B. linkedin, onoffice, mobilekey"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  onKeyDown={(e) => e.key === 'Enter' && runDebugMailSearch()}
                />
                <button
                  onClick={runDebugMailSearch}
                  disabled={debugLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {debugLoading ? '...' : 'Suchen'}
                </button>
              </div>
              
              {debugResult && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
                  {debugResult.success ? (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Mails in DB:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{debugResult.total_mails_in_db}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Treffer für "{debugQuery}":</span>
                        <span className={`font-medium ${debugResult.found_count > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {debugResult.found_count}
                        </span>
                      </div>
                      {debugResult.sample_mails && debugResult.sample_mails.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Beispiel-Mails:</p>
                          {debugResult.sample_mails.slice(0, 3).map((m, i) => (
                            <div key={i} className="text-xs text-gray-600 dark:text-gray-400 py-1">
                              [{m.date}] {m.subject} <span className="text-gray-400">({m.from})</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-2">DB: {debugResult.db_name}</div>
                    </>
                  ) : (
                    <div className="text-red-600 dark:text-red-400">
                      Fehler: {debugResult.error}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-sm flex items-center gap-2">
          <Check className="h-4 w-4" />
          {successMessage}
        </div>
      )}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Message List */}
        <div className={`${selectedMessage ? 'w-1/3 border-r border-gray-200 dark:border-gray-700' : 'w-full'} overflow-y-auto`}>
          {messages.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">Inbox ist leer</p>
              <p className="text-sm mt-1">Keine ausstehenden Entscheidungen</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  onClick={() => handleSelectMessage(msg)}
                  data-testid={`inbox-message-${msg.id}`}
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedMessage?.id === msg.id 
                      ? 'bg-blue-50 dark:bg-blue-900/20' 
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900 dark:text-white truncate">
                          {msg.from_name || msg.from_address}
                        </span>
                        {msg.has_attachments && (
                          <Paperclip className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-gray-900 dark:text-gray-100 truncate font-medium">
                        {msg.subject || '(Kein Betreff)'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                        {msg.snippet}
                      </p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="text-xs text-gray-400">
                          {formatDate(msg.received_datetime)}
                        </span>
                        {msg.owner_match_type === 'suggested' && (
                          <span className="text-xs px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded">
                            Eigentümer vermutet
                          </span>
                        )}
                        {msg.owner_match_type === 'exact' && (
                          <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                            {msg.owner_name}
                          </span>
                        )}
                        {msg.is_advertisement && (
                          <span className="text-xs px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">
                            Werbung ({Math.round(msg.ad_confidence * 100)}%)
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail / Decision Panel */}
        {selectedMessage && (
          <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
            <div className="p-6 space-y-6">
              {/* Message Info */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                  <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                    {selectedMessage.mailbox_email}
                  </span>
                  <span>{formatDate(selectedMessage.received_datetime)}</span>
                  
                  {/* Badge: Automatisch entschieden */}
                  {selectedMessage.rule_applied_id && (
                    <div className="relative group">
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded flex items-center gap-1 cursor-help" data-testid="auto-decided-badge">
                        <Zap className="h-3 w-3" />
                        Automatisch entschieden
                      </span>
                      {/* Tooltip */}
                      <div className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none">
                        <div className="font-medium mb-1">Regel: {selectedMessage.rule_applied_name}</div>
                        <div>Aktion: {selectedMessage.action_taken}</div>
                        <div>Datum: {selectedMessage.rule_applied_at?.substring(0, 19)}</div>
                        {selectedMessage.converted_from_delete && (
                          <div className="mt-1 text-yellow-300">
                            ⚠️ Auto-Delete deaktiviert → Archiv statt Löschen
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {selectedMessage.subject || '(Kein Betreff)'}
                </h2>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-3">
                  <User className="h-4 w-4" />
                  <span>{selectedMessage.from_name}</span>
                  <span className="text-gray-400">&lt;{selectedMessage.from_address}&gt;</span>
                </div>
                
                {/* Action Buttons: In Outlook öffnen / Original anzeigen */}
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => openInOutlook(selectedMessage.web_link)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
                    data-testid="open-in-outlook-btn"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    In Outlook öffnen
                  </button>
                  <button
                    onClick={() => loadOriginalMail(selectedMessage.id)}
                    disabled={loadingOriginal}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
                    data-testid="show-original-btn"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    {loadingOriginal ? 'Lädt...' : 'Original anzeigen'}
                  </button>
                </div>
                
                {/* Mail Body - vollständig oder Preview */}
                <div className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 rounded-lg p-3 max-h-96 overflow-y-auto">
                  {selectedMessage.body_content ? (
                    <div 
                      dangerouslySetInnerHTML={{ __html: selectedMessage.body_content }}
                      className="prose prose-sm dark:prose-invert max-w-none"
                    />
                  ) : selectedMessage.body_preview ? (
                    <p className="whitespace-pre-wrap">{selectedMessage.body_preview}</p>
                  ) : (
                    <p className="text-gray-400 italic">Kein Inhalt verfügbar</p>
                  )}
                </div>
                
                {/* Attachments */}
                {selectedMessage.has_attachments && selectedMessage.attachment_details?.length > 0 && (
                  <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                      <Paperclip className="h-4 w-4" />
                      Anhänge ({selectedMessage.attachment_details.length})
                    </h4>
                    <div className="space-y-2">
                      {selectedMessage.attachment_details.map((att, idx) => (
                        <div 
                          key={att.id || idx}
                          className="flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-700 rounded-lg"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                              {att.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({Math.round((att.size || 0) / 1024)} KB)
                            </span>
                          </div>
                          {att.id && (
                            <button
                              onClick={async () => {
                                try {
                                  const token = getToken();
                                  const response = await fetch(
                                    `${API}/mail/attachment/${selectedMessage.id}/${att.id}`,
                                    {
                                      headers: { Authorization: `Bearer ${token}` }
                                    }
                                  );
                                  
                                  if (!response.ok) {
                                    const error = await response.json();
                                    alert(`Download fehlgeschlagen: ${error.detail || 'Unbekannter Fehler'}`);
                                    return;
                                  }
                                  
                                  // Blob erstellen und Download auslösen
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = att.name;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                } catch (err) {
                                  console.error('Download error:', err);
                                  alert('Download fehlgeschlagen: ' + err.message);
                                }
                              }}
                              className="text-xs text-blue-600 hover:text-blue-700 flex-shrink-0"
                              data-testid={`download-attachment-${idx}`}
                            >
                              Download
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Fallback: Nur Attachment-Namen wenn keine Details */}
                {selectedMessage.has_attachments && !selectedMessage.attachment_details?.length && selectedMessage.attachment_names?.length > 0 && (
                  <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                      <Paperclip className="h-4 w-4" />
                      Anhänge ({selectedMessage.attachment_names.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedMessage.attachment_names.map((name, idx) => (
                        <span 
                          key={idx}
                          className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-400"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedMessage.owner_match_type === 'suggested' && (
                  <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">Möglicher Eigentümer erkannt</span>
                    </div>
                    <p className="text-sm text-yellow-600 dark:text-yellow-500 mt-1">
                      Vermuteter Eigentümer: <strong>{selectedMessage.owner_name}</strong>
                    </p>
                  </div>
                )}
              </div>

              {/* Decision Form */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Check className="h-5 w-5 text-blue-500" />
                  Entscheidung treffen
                </h3>

                {/* Smart Suggestion (v2.9.7, erweitert v2.9.10) */}
                {smartSuggestion && !loadingSuggestion && (
                  <div className="mb-5" data-testid="smart-suggestion-panel">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      {smartSuggestion.auto_executed ? 'Automatisch verarbeitet' : 'Vorschlag'}
                    </p>
                    {smartSuggestion.auto_executed ? (
                      <div className="border-2 border-green-300 dark:border-green-700 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-xl p-3" data-testid="auto-executed-badge">
                        <div className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                          <span className="text-sm font-semibold text-green-800 dark:text-green-200">
                            Automatisch verarbeitet: {smartSuggestion.quick_action?.button_label}
                          </span>
                        </div>
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                          Confidence: {smartSuggestion.confidence}% — {smartSuggestion.basis_text}
                        </p>
                      </div>
                    ) : (
                      <div className="border-2 border-cyan-300 dark:border-cyan-700 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30 rounded-xl p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                            <span className="text-sm font-semibold text-cyan-800 dark:text-cyan-200">
                              Empfehlung: {smartSuggestion.quick_action?.button_label}
                            </span>
                          </div>
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              smartSuggestion.confidence >= 90
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                : smartSuggestion.confidence >= 80
                                ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300'
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                            }`}
                            data-testid="suggestion-confidence-badge"
                          >
                            {smartSuggestion.confidence}% Confidence
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {smartSuggestion.basis_text}
                        </p>
                        <button
                          onClick={() => handleQuickDecide(smartSuggestion.quick_action?.slug, true)}
                          disabled={quickDeciding !== null || deciding}
                          data-testid="smart-suggestion-apply-btn"
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg transition-all border-2 border-cyan-400 dark:border-cyan-600 bg-cyan-500 hover:bg-cyan-600 dark:bg-cyan-600 dark:hover:bg-cyan-500 text-white disabled:opacity-50 shadow-sm"
                        >
                          {quickDeciding === smartSuggestion.quick_action?.slug ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Zap className="h-4 w-4" />
                          )}
                          {smartSuggestion.quick_action?.button_label} anwenden
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {loadingSuggestion && (
                  <div className="mb-5 flex items-center gap-2 text-xs text-gray-400" data-testid="suggestion-loading">
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    Analysiere Muster...
                  </div>
                )}

                {/* 1-Klick Entscheidungen (v2.9.6: dynamisch aus DB) */}
                {quickActions.length > 0 && (
                <div className="mb-5" data-testid="quick-actions-panel">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    1-Klick Aktionen
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {quickActions.map((qa) => {
                      const colorMap = {
                        emerald: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40',
                        red: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40',
                        purple: 'border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/40',
                        blue: 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40',
                        amber: 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40',
                        gray: 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
                      };
                      const iconMap = {
                        FileText, Trash2, Zap, Users, Tag, Mail, Forward, Check, AlertCircle,
                      };
                      const IconComponent = iconMap[qa.icon] || Tag;
                      const colorClass = colorMap[qa.color_variant] || colorMap.gray;
                      
                      return (
                        <button
                          key={qa.id || qa.slug}
                          onClick={() => handleQuickDecide(qa.slug)}
                          disabled={quickDeciding !== null || deciding}
                          data-testid={`quick-action-${qa.slug}`}
                          className={`flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded-lg transition-all border disabled:opacity-50 ${colorClass}`}
                        >
                          {quickDeciding === qa.slug ? (
                            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <IconComponent className="h-3.5 w-3.5" />
                          )}
                          {qa.button_label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                )}

                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mb-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Detaillierte Entscheidung
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Decision Templates (Quick Select) */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Schnellauswahl (Templates)
                      </label>
                      {isAdmin && (
                        <button
                          onClick={openCreateTemplateModal}
                          className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                          data-testid="add-template-btn"
                        >
                          <Plus className="h-3 w-3" />
                          Neue Kategorie
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {loadingTemplates ? (
                        <span className="text-sm text-gray-500">Lade Templates...</span>
                      ) : (
                        templates.filter(t => t.active).map((template) => (
                          <div key={template.id} className="relative group">
                            <button
                              onClick={() => applyTemplate(template)}
                              data-testid={`template-btn-${template.id}`}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                decision.template_id === template.id
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                              }`}
                              style={template.color ? { 
                                backgroundColor: decision.template_id === template.id ? undefined : template.color + '20',
                                borderColor: template.color
                              } : {}}
                            >
                              {template.name}
                            </button>
                            {isAdmin && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditTemplateModal(template);
                                }}
                                className="absolute -top-1 -right-1 hidden group-hover:flex h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded-full items-center justify-center hover:bg-gray-300"
                                data-testid={`edit-template-btn-${template.id}`}
                              >
                                <Edit2 className="h-3 w-3 text-gray-600 dark:text-gray-300" />
                              </button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                    {decision.template_id && (
                      <p className="text-xs text-gray-500 mt-1">
                        Template angewendet - Werte können unten angepasst werden
                      </p>
                    )}
                  </div>

                  {/* Eigentümer-Zuordnung (Manual) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <Users className="h-4 w-4 inline mr-1" />
                      Eigentümer zuordnen
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={ownerSearch}
                        onChange={(e) => {
                          setOwnerSearch(e.target.value);
                          setShowOwnerDropdown(true);
                        }}
                        onFocus={() => setShowOwnerDropdown(true)}
                        placeholder="Nach Name oder E-Mail suchen..."
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        data-testid="owner-search-input"
                      />
                      {loadingOwners && (
                        <RefreshCw className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-gray-400" />
                      )}
                      {showOwnerDropdown && owners.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {owners.map((owner) => (
                            <button
                              key={owner.id}
                              onClick={() => {
                                setDecision(prev => ({ ...prev, owner_id: owner.id }));
                                setOwnerSearch(owner.name);
                                setShowOwnerDropdown(false);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
                            >
                              <div className="font-medium text-gray-900 dark:text-white">{owner.name}</div>
                              <div className="text-xs text-gray-500">{owner.emails.join(', ')}</div>
                              {owner.mail_count > 0 && (
                                <span className="text-xs text-gray-400">{owner.mail_count} Mails</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {decision.owner_id && (
                      <button
                        onClick={() => {
                          setDecision(prev => ({ ...prev, owner_id: '' }));
                          setOwnerSearch('');
                        }}
                        className="text-xs text-red-600 hover:text-red-700 mt-1"
                      >
                        Zuordnung entfernen
                      </button>
                    )}
                  </div>

                  {/* Kategorie */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Kategorie *
                    </label>
                    <select
                      value={decision.category}
                      onChange={(e) => setDecision({ ...decision, category: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      data-testid="category-select"
                    >
                      <option value="">Bitte wählen...</option>
                      {categoryOptions.map((cat) => (
                        <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                      ))}
                    </select>
                  </div>

                  {/* Sichtbarkeit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Sichtbarkeit *
                    </label>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setDecision({ ...decision, email_visibility: 'admin_only' })}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          decision.email_visibility === 'admin_only'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                        data-testid="visibility-admin-only"
                      >
                        Nur Admin
                      </button>
                      <button
                        onClick={() => setDecision({ ...decision, email_visibility: 'admin_employee' })}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          decision.email_visibility === 'admin_employee'
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                        data-testid="visibility-admin-employee"
                      >
                        Admin + Mitarbeiter
                      </button>
                    </div>
                  </div>

                  {/* Aktion */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Aktion *
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {actions.map((act) => (
                        <button
                          key={act.value}
                          onClick={() => setDecision({ ...decision, action: act.value })}
                          data-testid={`action-${act.value}`}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            decision.action === act.value
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          <act.icon className="h-4 w-4" />
                          {act.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Forward To */}
                  {decision.action === 'forward' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Weiterleiten an
                      </label>
                      <input
                        type="email"
                        value={decision.forward_to}
                        onChange={(e) => setDecision({ ...decision, forward_to: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  )}

                  {/* Auto-Archive Toggle (v2.9.4) */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="auto-archive-toggle">
                      <input
                        type="checkbox"
                        checked={autoArchive}
                        onChange={(e) => setAutoArchive(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Nach Entscheidung archivieren
                      </span>
                    </label>
                    <p className="text-xs text-gray-400 mt-1 ml-6">
                      Mail wird nach der Entscheidung aus dem Outlook-Posteingang in das Archiv verschoben
                    </p>
                  </div>

                  {/* KI-Antwortvorschlag (v2.9.8) */}
                  {selectedMessage && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <button
                        onClick={handleGenerateAiReply}
                        disabled={generatingAiReply}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 rounded-lg text-sm font-semibold hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors border border-violet-200 dark:border-violet-800"
                        data-testid="generate-ai-reply-btn"
                      >
                        {generatingAiReply ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Zap className="h-4 w-4" />
                        )}
                        KI-Antwortvorschlag erstellen
                      </button>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-center">
                        GPT analysiert die Mail und erstellt einen Entwurf
                      </p>
                    </div>
                  )}

                  {/* Antwort-Entwurf erstellen (v2.9.4) */}
                  {selectedMessage && replyTemplates.length > 0 && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <button
                        onClick={() => {
                          setShowReplyDraftModal(true);
                          setSelectedReplyTemplate(null);
                          setDraftResult(null);
                        }}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded-lg text-sm font-medium hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors border border-amber-200 dark:border-amber-800"
                        data-testid="create-reply-draft-btn"
                      >
                        <Mail className="h-4 w-4" />
                        Antwort-Entwurf erstellen
                      </button>
                      {selectedMessage.reply_draft_template_name && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 text-center">
                          Letzter Entwurf: "{selectedMessage.reply_draft_template_name}"
                        </p>
                      )}
                    </div>
                  )}

                  {/* Regel erstellen */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={decision.create_rule}
                        onChange={(e) => setDecision({ ...decision, create_rule: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Regel für zukünftige Mails erstellen</span>
                    </label>

                    {decision.create_rule && (
                      <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Regel-Typ</label>
                          <select
                            value={decision.rule_match_type}
                            onChange={(e) => setDecision({ ...decision, rule_match_type: e.target.value })}
                            className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                          >
                            <option value="sender">Absender-Adresse</option>
                            <option value="domain">Domain</option>
                            <option value="subject">Betreff enthält</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Wert</label>
                          <input
                            type="text"
                            value={decision.rule_match_value}
                            onChange={(e) => setDecision({ ...decision, rule_match_value: e.target.value })}
                            className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                          />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={decision.rule_approved}
                            onChange={(e) => setDecision({ ...decision, rule_approved: e.target.checked })}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600"
                          />
                          <span className="text-xs text-gray-600 dark:text-gray-400">Regel sofort freigeben</span>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* E-Mail zu Eigentümer hinzufügen */}
                  {selectedMessage.owner_match_type === 'suggested' && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={decision.add_owner_email}
                          onChange={(e) => setDecision({ ...decision, add_owner_email: e.target.checked })}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          E-Mail zu Eigentümer "{selectedMessage.owner_name}" hinzufügen
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    onClick={handleDecide}
                    disabled={deciding || !decision.category || !decision.email_visibility || !decision.action}
                    className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="save-decision-btn"
                  >
                    {deciding ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Wird gespeichert...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4" />
                        Entscheidung speichern
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Reply-Draft Modal (v2.9.4) */}
      {showReplyDraftModal && selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Antwort-Entwurf erstellen
              </h3>
              <button
                onClick={() => setShowReplyDraftModal(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                data-testid="close-reply-draft-modal"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Original-Mail Info */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Antwort auf:</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {selectedMessage.subject}
                </p>
                <p className="text-xs text-gray-500">
                  Von: {selectedMessage.from_name || selectedMessage.from_address}
                </p>
              </div>
              
              {/* Template-Auswahl */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Textbaustein wählen
                </label>
                <div className="space-y-2">
                  {replyTemplates.map((tmpl) => (
                    <button
                      key={tmpl.id}
                      onClick={() => setSelectedReplyTemplate(tmpl)}
                      data-testid={`reply-template-${tmpl.id}`}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors border ${
                        selectedReplyTemplate?.id === tmpl.id
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="font-medium">{tmpl.name}</div>
                      {tmpl.description && (
                        <div className="text-xs text-gray-500 mt-0.5">{tmpl.description}</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Vorschau */}
              {selectedReplyTemplate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Vorschau
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap max-h-40 overflow-y-auto">
                    {selectedReplyTemplate.body_template
                      .replace(/\{\{absender_name\}\}/g, selectedMessage.from_name || selectedMessage.from_address || '')
                      .replace(/\{\{absender_email\}\}/g, selectedMessage.from_address || '')
                      .replace(/\{\{betreff\}\}/g, selectedMessage.subject || '')
                      .replace(/\{\{eigentuemer_name\}\}/g, selectedMessage.owner_name || '')
                      .replace(/\{\{datum\}\}/g, new Date().toLocaleDateString('de-DE'))
                    }
                  </div>
                </div>
              )}

              {/* Hinweis */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-300">
                Der Entwurf wird in Outlook unter "Entwürfe" gespeichert. Er wird NICHT automatisch gesendet.
              </div>
              
              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReplyDraftModal(false)}
                  className="flex-1 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => selectedReplyTemplate && createReplyDraft(selectedMessage.id, selectedReplyTemplate.id)}
                  disabled={!selectedReplyTemplate || creatingDraft}
                  className="flex-1 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="confirm-create-draft-btn"
                >
                  {creatingDraft ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Wird erstellt...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      Entwurf erstellen
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KI-Antwortvorschlag Modal (v2.9.8) */}
      {showAiReplyModal && selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="ai-reply-modal">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  KI-Antwortvorschlag
                </h3>
              </div>
              <button
                onClick={handleDiscardAiReply}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                data-testid="close-ai-reply-modal"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Original-Mail Info */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Antwort auf:</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {selectedMessage.subject}
                </p>
                <p className="text-xs text-gray-500">
                  Von: {selectedMessage.from_name || selectedMessage.from_address}
                </p>
              </div>

              {/* Ladezustand */}
              {generatingAiReply && (
                <div className="flex flex-col items-center justify-center py-10 gap-3" data-testid="ai-reply-loading">
                  <RefreshCw className="h-8 w-8 text-violet-500 animate-spin" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    KI analysiert die Mail und erstellt einen Vorschlag...
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    Dies kann einige Sekunden dauern
                  </p>
                </div>
              )}

              {/* Fehler */}
              {aiReplyResult && !aiReplyResult.success && !generatingAiReply && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4" data-testid="ai-reply-error">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-700 dark:text-red-300">Fehler bei der KI-Generierung</p>
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">{aiReplyResult.error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Erfolg: Antwortvorschlag */}
              {aiReplyResult?.success && !generatingAiReply && (
                <>
                  {/* Sensibilitaets-Warnung */}
                  {aiReplyResult.sensitivity?.is_sensitive && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-3 flex items-start gap-2" data-testid="ai-reply-sensitivity-warning">
                      <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                          Sensibler Inhalt erkannt
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                          Bitte vor Versand besonders sorgfaeltig pruefen. Erkannte Themen: {aiReplyResult.sensitivity.keywords_found?.join(', ')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Allgemeiner Hinweis */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2.5 text-xs text-blue-700 dark:text-blue-300 flex items-center gap-2">
                    <Eye className="h-3.5 w-3.5 flex-shrink-0" />
                    Bitte den Vorschlag vor dem Versand pruefen. Text kann bearbeitet werden.
                  </div>

                  {/* Editierbarer Antworttext */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Antwortvorschlag
                      <span className="text-xs text-gray-400 dark:text-gray-500 ml-2 font-normal">
                        (Modell: {aiReplyResult.model_used})
                      </span>
                    </label>
                    <textarea
                      value={aiReplyText}
                      onChange={(e) => setAiReplyText(e.target.value)}
                      className="w-full h-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-y focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      data-testid="ai-reply-textarea"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {aiReplyText.length} Zeichen
                    </p>
                  </div>

                  {/* Hinweis: Entwurf nicht automatisch senden */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2.5 text-xs text-gray-500 dark:text-gray-400">
                    Der Entwurf wird in Outlook unter "Entwuerfe" gespeichert und NICHT automatisch gesendet.
                  </div>

                  {/* Aktions-Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleDiscardAiReply}
                      className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      data-testid="discard-ai-reply-btn"
                    >
                      Verwerfen
                    </button>
                    <button
                      onClick={handleCreateAiDraft}
                      disabled={!aiReplyText.trim() || creatingAiDraft}
                      className="flex-1 py-2.5 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                      data-testid="create-ai-draft-btn"
                    >
                      {creatingAiDraft ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Wird erstellt...
                        </>
                      ) : (
                        <>
                          <Mail className="h-4 w-4" />
                          Als Outlook-Entwurf speichern
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}

              {/* Bei Fehler: Nur Schliessen Button */}
              {aiReplyResult && !aiReplyResult.success && !generatingAiReply && (
                <button
                  onClick={handleDiscardAiReply}
                  className="w-full py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Schliessen
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Template Create/Edit Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingTemplate ? 'Kategorie bearbeiten' : 'Neue Kategorie erstellen'}
              </h3>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  disabled={editingTemplate?.is_system_default}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                  placeholder="z.B. Anwalt, Steuerbüro, ..."
                  data-testid="template-name-input"
                />
                {editingTemplate?.is_system_default && (
                  <p className="text-xs text-gray-500 mt-1">System-Template: Name kann nicht geändert werden</p>
                )}
              </div>

              {/* Prompt/Guidance Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Prompt / Guidance-Text
                </label>
                <textarea
                  value={templateForm.prompt_text}
                  onChange={(e) => setTemplateForm({ ...templateForm, prompt_text: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  placeholder="Beschreibung für KI-Assistenz bei Antwort-Entwürfen..."
                  data-testid="template-prompt-input"
                />
              </div>

              {/* Default Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Standard-Kategorie
                </label>
                <select
                  value={templateForm.default_category}
                  onChange={(e) => setTemplateForm({ ...templateForm, default_category: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {categoryOptions.map((cat) => (
                    <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                  ))}
                </select>
              </div>

              {/* Default Visibility */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Standard-Sichtbarkeit
                </label>
                <select
                  value={templateForm.default_visibility}
                  onChange={(e) => setTemplateForm({ ...templateForm, default_visibility: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="admin_only">Nur Admin</option>
                  <option value="admin_employee">Admin + Mitarbeiter</option>
                </select>
              </div>

              {/* AI Context Hint */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  KI-Kontext (optional)
                </label>
                <input
                  type="text"
                  value={templateForm.ai_context_hint}
                  onChange={(e) => setTemplateForm({ ...templateForm, ai_context_hint: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  placeholder="z.B. juristisch, formell, sachlich"
                />
              </div>

              {/* Active Toggle */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={templateForm.active}
                    onChange={(e) => setTemplateForm({ ...templateForm, active: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Aktiv (in Schnellauswahl anzeigen)</span>
                </label>
              </div>

              {/* Last Updated Info */}
              {editingTemplate?.updated_at && (
                <div className="text-xs text-gray-500 border-t border-gray-200 dark:border-gray-700 pt-3">
                  Zuletzt geändert: {formatDate(editingTemplate.updated_at)}
                  {editingTemplate.updated_by && ` von ${editingTemplate.updated_by}`}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              {/* Delete/Deactivate Button (nur bei Bearbeitung) */}
              {editingTemplate && (
                <button
                  onClick={async () => {
                    const action = editingTemplate.is_system_default ? 'deaktivieren' : 'löschen';
                    if (!confirm(`Kategorie "${editingTemplate.name}" wirklich ${action}?`)) return;
                    
                    try {
                      const token = getToken();
                      await axios.delete(`${API}/mail/templates/${editingTemplate.id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setSuccessMessage(editingTemplate.is_system_default 
                        ? 'Kategorie deaktiviert' 
                        : 'Kategorie gelöscht');
                      setShowTemplateModal(false);
                      await loadTemplates();
                      setTimeout(() => setSuccessMessage(null), 3000);
                    } catch (err) {
                      alert(err.response?.data?.detail || 'Fehler beim Löschen');
                    }
                  }}
                  className="px-4 py-2 border border-red-300 dark:border-red-600 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  data-testid="delete-template-btn"
                >
                  {editingTemplate.is_system_default ? 'Deaktivieren' : 'Löschen'}
                </button>
              )}
              <button
                onClick={() => setShowTemplateModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Abbrechen
              </button>
              <button
                onClick={saveTemplate}
                disabled={savingTemplate || !templateForm.name.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-template-btn"
              >
                {savingTemplate ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Speichern...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Speichern
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Original Mail Modal */}
      {showOriginalModal && originalContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="original-mail-modal">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-[90vw] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Original-Mail
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {originalContent.subject}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {originalContent.web_link && (
                  <button
                    onClick={() => openInOutlook(originalContent.web_link)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg transition-colors"
                    data-testid="modal-open-in-outlook-btn"
                  >
                    <ExternalLink className="h-4 w-4" />
                    In Outlook öffnen
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowOriginalModal(false);
                    setOriginalContent(null);
                  }}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  data-testid="close-original-modal-btn"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Meta Info */}
              <div className="mb-4 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <div><strong>Von:</strong> {originalContent.from_name} &lt;{originalContent.from_address}&gt;</div>
                <div><strong>An:</strong> {originalContent.to_addresses?.join(', ') || '-'}</div>
                <div><strong>Datum:</strong> {originalContent.received_datetime?.substring(0, 19)?.replace('T', ' ')}</div>
                <div><strong>Typ:</strong> {originalContent.body_content_type === 'html' ? 'HTML' : 'Text'}</div>
              </div>
              
              {/* Divider */}
              <hr className="my-4 border-gray-200 dark:border-gray-700" />
              
              {/* Body Content */}
              <div className="prose prose-sm dark:prose-invert max-w-none">
                {originalContent.body_content_type === 'html' ? (
                  <div 
                    dangerouslySetInnerHTML={{ __html: originalContent.body_content }}
                    className="mail-original-content"
                  />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto">
                    {originalContent.body_content || 'Kein Inhalt verfügbar'}
                  </pre>
                )}
              </div>
              
              {/* Attachments */}
              {originalContent.has_attachments && originalContent.attachments?.length > 0 && (
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Anhänge ({originalContent.attachments.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {originalContent.attachments.map((att, idx) => (
                      <span 
                        key={idx}
                        className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-sm text-gray-600 dark:text-gray-300 rounded"
                      >
                        {att.name} ({Math.round((att.size || 0) / 1024)} KB)
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
